import React, { useContext } from 'react';
import { Case } from '../../../../services/buildfire/rdb/cases';
import styled from 'styled-components';
import { useFirebase } from '../../../../services/firebase';
import { useActor, useInterpret } from '@xstate/react';
import { editCaseTypeMachine } from '../../../../stateMachines/editCaseType';
import { CaseType } from '../../../../stateMachines/submitCase/caseType';
import { useIonToast } from '@ionic/react';
import CaseContext from '../../../../services/buildfire/rdb/CaseContext';
import { CaseTypeSelection } from '../../../submitCase/CaseTypeSelection';
import CaseCheckout from '../../CaseCheckout';
import { FormValues } from '../../CaseForm';
import useGoBackToCase from '../../../../hooks/useGoBackToCase';
import ProgressBar from '../../../common/ProgressBar';
import { PaymentType } from '../../../../stateMachines/submitCase/selectPaymentType';
import * as Str from '../../../../util/string';

interface EditCaseTypeProps {
  case: Case;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const EditCaseType: React.FC<EditCaseTypeProps> = ({ case: c }) => {
  const { currentUser, app, parentAccount } = useFirebase();
  // Need `caseInfo` on the context since `editCase/checkout.ts` uses these values
  // to send to the updateCase endpoint
  const caseInfo: FormValues = {
    ...c,
    submitterEmailAddresses: Str.dedup([
      c.submitter.email || '',
      ...(c.submitter.additionalEmails || []),
    ]),
    submitterFirstName: c.submitter.first || '',
    submitterLastName: c.submitter.last || '',
    patientFirstName: c.patient.first || '',
    patientLastName: c.patient.last || '',
    internalReferenceNumber: c.internalReferenceNumber || '',
  };
  const editCaseTypeService = useInterpret(editCaseTypeMachine, {
    context: {
      case: c,
      firebaseApp: app,
      userId: currentUser?.uid!,
      caseType: c.priority ? CaseType.PRIORITY : CaseType.STANDARD,
      parentAccount,
      caseInfo,
      name: currentUser?.displayName!,
      email: currentUser?.email!,
      selectedInvoiceAccountUid: c.invoiceAccount?.uid,
      paymentType: c.invoiceAccount
        ? PaymentType.INVOICE
        : PaymentType.CREDIT_CARD,
    },
  });
  const [state, send] = useActor(editCaseTypeService);
  const [present] = useIonToast();
  const { updateCase: updateCaseLocally } = useContext(CaseContext);

  (window as any).editCaseTypeService = editCaseTypeService;

  const isLoading =
    state.matches({ caseType: 'load' }) || state.matches({ checkout: 'load' });
  const isSubmitting = state.matches({ checkout: 'submitCase' });
  const goBack = useGoBackToCase();

  React.useEffect(() => {
    const onDone = () => {
      updateCaseLocally({
        ...c,
        priority:
          editCaseTypeService.state.context.caseType === CaseType.PRIORITY,
      });
      goBack(c.id);

      present({
        message: 'Case updated successfully.',
        duration: 5000,
        position: 'bottom',
      });
    };
    editCaseTypeService.onDone(onDone);
    return () => {
      editCaseTypeService.off(onDone);
    };
  }, []);

  if (isLoading) {
    return (
      <Container>
        <ProgressBar indeterminate={isLoading} />
      </Container>
    );
  }

  return (
    <Container>
      {state.matches('caseType') && (
        <CaseTypeSelection
          title="Edit Case Type"
          currentUser={currentUser!}
          isLoading={isLoading}
          onNext={() => send({ type: 'NEXT' })}
          onChange={(value) => send({ type: 'SET_CASE_TYPE', caseType: value })}
          value={state.context.caseType}
          isPriorityAvailable={state.context.priorityAvailable}
          businessStartTime={state.context.businessStart}
          businessEndTime={state.context.businessEnd}
          priorityFee={state.context.priorityFeePrice}
          onBack={() => goBack(c.id)}
          initialValue={c.priority ? CaseType.PRIORITY : CaseType.STANDARD}
          preventNextIfUnchanged
        />
      )}

      {state.matches('checkout') && (
        <CaseCheckout
          caseUserId={state.context.userId}
          clinicName={state.context.name}
          clinicEmail={state.context.email}
          invoicingEnabled={!!c.invoiceAccount}
          parentAccount={state.context.parentAccount}
          onSubmit={() => send('NEXT')}
          onBack={() => send('BACK')}
          caseInfo={caseInfo}
          isSubmitting={isSubmitting}
          progress={state.context.uploadProgress}
          submitText="Update Case"
          selectedInvoiceAccountUid={state.context.selectedInvoiceAccountUid}
          lineItems={state.context.lineItems || []}
          total={state.context.totalPrice || 0}
          priorityUpgradePrice={state.context.priorityUpgradePrice || 0}
          error={state.context.error}
        />
      )}
    </Container>
  );
};

export default EditCaseType;
